import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { T } from 'components/common/text';
import CustomSpan from 'components/common/text/CustomSpan';
import { PartnerActions, ProjectActions } from 'store/actionCreators';
import { IProjectInfo } from 'lib/types/projectTypes';
import { IconButton } from '@material-ui/core';
import { useShallowAppSelector } from 'store/hooks';
import { IUserProfile } from 'lib/types/userTypes';
import { icon_circle_warning } from 'components/base/images';
import ClinicModal from 'components/common/modal/ClinicModal';
import { useHistory } from 'react-router-dom';
import AsyncFetchSelectSyncPartner from 'components/common/select/AsyncFetchSelectSyncPartner';
import { useDidUpdateEffect } from 'lib/utils';

interface ProjectSyncPartnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  useLabel?: boolean;
  useReset?: boolean;
  value: number | '';
  setValue: React.Dispatch<number | ''>;
  returnPath?: 'detail' | 'edit';
}

function ProjectSyncPartner({
  size = 'sm',
  useLabel = false,
  useReset = true,
  value = '',
  setValue,
  returnPath = 'detail',
}: ProjectSyncPartnerProps) {
  const {
    user,
    projectData: { projectInfo: projectInfo },
    createProjectSync: {
      payload: { partnerGroupIdx: creatSyncPartenr },
    },
    createProjectSyncSuccess,
    resetProjectSyncSuccess,
  }: {
    user: IUserProfile;
    projectData: { projectInfo: IProjectInfo };
    createProjectSync: { payload: { partnerGroupIdx: number } };
    createProjectSyncSuccess: boolean;
    resetProjectSyncSuccess: boolean;
  } = useShallowAppSelector(state => ({
    user: state.user.user || {},
    projectData: state.project.project.data || { projectInfo: {} },
    createProjectSync: state.project.createProjectSync.data || { payload: {} },
    createProjectSyncSuccess: state.project.createProjectSync.success,
    resetProjectSyncSuccess: state.project.resetProjectSync.success,
  }));
  // SECTION: state
  const [openNewSyncModal, setOpenNewSyncModal] = useState<boolean>(false);
  const [newSyncPartner, setNewSyncPartner] = useState<number | ''>('');

  const syncInfo = projectInfo?.syncInfo || null;
  const partnerIdx = projectInfo?.partnerIdx || null;
  const partnerName = projectInfo?.partnerName;
  const projectCode = projectInfo?.projectCode;
  const history = useHistory();

  const { t } = useTranslation();

  const partnerGroupIdx = partnerIdx || syncInfo?.userGroupIdx || '';
  const partnerNameValue = partnerName || syncInfo?.partnerName || '';
  const [resetState, setResetState] = useState<boolean>(false);
  const [selectedPartnerValue, setSelectedPartnerValue] = useState<number | ''>(value);

  const [selectedPartnerName, setSelectedPartnerName] = useState<string>('');
  const [prevSelectedPartnerName, setPrevSelectedPartnerName] = useState<string>('');
  const [selectedPartnerState, setSelectedPartnerState] = useState<string>('');
  const [prevSelectedPartnerState, setPrevSelectedPartnerState] = useState<string>('');

  useDidUpdateEffect(() => {
    if (!createProjectSyncSuccess) return;
    setValue(creatSyncPartenr);
    ProjectActions.fetch_project_request(projectCode);
  }, [createProjectSyncSuccess, creatSyncPartenr, projectCode]);

  useDidUpdateEffect(() => {
    if (!resetProjectSyncSuccess) return;
    setValue('');
    ProjectActions.fetch_project_request(projectCode);
    PartnerActions.fetch_sync_partners_request({
      keyword: '',
      page: 1,
    });
  }, [resetProjectSyncSuccess]);

  useEffect(() => {
    if (!!projectInfo.partnerIdx) {
      setSelectedPartnerState(`${t('SYNC_PARTNER_STATE_ACCEPT')}`);
      setPrevSelectedPartnerState(`${t('SYNC_PARTNER_STATE_ACCEPT')}`);
    } else if (!!syncInfo) {
      setSelectedPartnerState(`${t('SYNC_PARTNER_STATE_REQ')}`);
      setPrevSelectedPartnerState(`${t('SYNC_PARTNER_STATE_REQ')}`);
    } else {
      setSelectedPartnerState('');
      setPrevSelectedPartnerState('');
    }
  }, [projectInfo.partnerIdx, syncInfo]);

  useEffect(() => {
    if (!!projectInfo?.partnerName) {
      setSelectedPartnerName(projectInfo?.partnerName);
      setPrevSelectedPartnerName(projectInfo?.partnerName);
    } else if (!!syncInfo) {
      setSelectedPartnerName(syncInfo.partnerName);
      setPrevSelectedPartnerName(syncInfo.partnerName);
    } else {
      setSelectedPartnerName('');
      setPrevSelectedPartnerName('');
    }
  }, [projectInfo.partnerName, syncInfo]);

  useEffect(() => {
    if (!!projectInfo?.partnerIdx) {
      setSelectedPartnerValue(projectInfo?.partnerIdx);
    } else if (!!syncInfo) {
      setSelectedPartnerValue(syncInfo?.userGroupIdx);
    } else {
      setSelectedPartnerValue('');
    }
  }, [projectInfo?.partnerIdx, syncInfo]);

  // SECTION: variable

  // SECTION: declare hooks

  // SECTION: declare function

  useEffect(() => {
    setValue(selectedPartnerValue);
  }, [selectedPartnerValue]);

  useEffect(() => {
    if (!value || !partnerGroupIdx) return;
    if (value !== partnerGroupIdx) {
      setNewSyncPartner(value);
      handleOpenNewSync();
    }
  }, [value]);

  useEffect(() => {
    //sync
    if (!!syncInfo) {
      setResetState(false);
    } else if (!!syncInfo && !value) {
      setResetState(false);
    } else {
      setResetState(true);
    }
  }, [syncInfo, value]);

  const handleClickResetSync = () => {
    if (!syncInfo) return;
    ProjectActions.reset_project_sync_request({
      projectCode,
      data: { partnerGroupIdx: syncInfo.userGroupIdx },
    });
  };

  const handleCloseNewSync = () => {
    setOpenNewSyncModal(false);
    setValue(selectedPartnerValue);
    setSelectedPartnerName(prevSelectedPartnerName);
    setSelectedPartnerState(prevSelectedPartnerState);
  };

  const handleConfirmNewSync = () => {
    setOpenNewSyncModal(false);
    history.push(
      `/project/creating/${projectInfo?.projectCode}?pathTo=copy&copyPartner=${newSyncPartner}&returnPath=${returnPath}`,
    );
  };

  const handleOpenNewSync = () => {
    setOpenNewSyncModal(true);
  };

  return (
    <StyledProjectSyncPartner data-component-name="ProjectSyncPartner">
      <ClinicModal
        title={t('CLINIC_MODAL_TITLE_NOTICE')}
        isOpen={openNewSyncModal}
        onClose={handleCloseNewSync}
        onCancel={handleCloseNewSync}
        onConfirm={handleConfirmNewSync}
      >
        <NewSyncConfirmModal partnerName={partnerNameValue} newPartnerName={selectedPartnerName} />
      </ClinicModal>
      {useLabel && (
        <div className="projectSyncPartner__label">
          <CustomSpan fontSize={14}>
            <T>GLOBAL_PARTNER</T>
          </CustomSpan>
        </div>
      )}
      <AsyncFetchSelectSyncPartner
        className={cx(`${size} projectSyncPartner__select`, {
          widthType1: !useLabel && !useReset,
          widthType2: !useLabel && !!useReset,
          widthType3: !!useLabel && !useReset,
          widthType4: !!useLabel && !!useReset,
        })}
        isOpenUp
        placeholder={t('SELECT_PARTNER')}
        idKey={'userGroupIdx'}
        labelKey={'name'}
        exceptId={user.userGroupIdx}
        //
        partnerGroupIdx={partnerGroupIdx}
        syncInfo={syncInfo}
        projectPartner={partnerIdx}
        selectedValue={value}
        setSelectedValue={setValue}
        selectedSyncState={selectedPartnerState}
        setSelectedSyncState={setSelectedPartnerState}
        selectedName={selectedPartnerName}
        setSelectedName={setSelectedPartnerName}
      />
      {useReset && (
        <IconButton
          className={cx(`${size} projectSyncPartner__cancel`, {})}
          disabled={resetState}
          onClick={handleClickResetSync}
        >
          <CustomSpan fontSize={14} fontWeight={400}>
            <T>GLOBAL_RESET</T>
          </CustomSpan>
        </IconButton>
      )}
    </StyledProjectSyncPartner>
  );
}

const StyledProjectSyncPartner = styled.div`
  width: 100%;
  height: 100%;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  .projectSyncPartner__label {
    width: 100px;
  }
  .projectSyncPartner__select {
    &.widthType1 {
      // label X, reset X
      width: 100%;
    }
    &.widthType2 {
      // label X, reset O
      width: calc(100% - 70px);
    }
    &.widthType3 {
      // label O, reset X
      width: calc(100% - 110px);
    }
    &.widthType4 {
      // label O, reset O
      width: calc(100% - 180px);
    }
  }
  .projectSyncPartner__cancel {
    width: 60px;
    height: 32px;
    border-radius: 3px;
    padding: 0;
    border: 1px solid #e1e1e7;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .projectSyncPartner__cancel {
    &.xs {
      height: 24px;
      font-size: 12px;
      .AsyncFetchSelect__dropdown_box {
        &.isOpenUp {
          bottom: 24px;
        }
        &.isOpenDown {
          top: 23px;
        }
      }
    }
    &.sm {
      height: 32px;
      font-size: 12px;
      .AsyncFetchSelect__dropdown_box {
        &.isOpenUp {
          bottom: 32px;
        }
        &.isOpenDown {
          top: 31px;
        }
      }
    }
    &.md {
      height: 40px;
      font-size: 14px;
      .AsyncFetchSelect__dropdown_box {
        &.isOpenUp {
          bottom: 40px;
        }
        &.isOpenDown {
          top: 39px;
        }
      }
    }
    &.lg {
      height: 46px;
      font-size: 16px;
      .AsyncFetchSelect__dropdown_box {
        &.isOpenUp {
          bottom: 46px;
        }
        &.isOpenDown {
          top: 45px;
        }
      }
    }
    &.xl {
      height: 56px;
      font-size: 16px;
      .AsyncFetchSelect__dropdown_box {
        &.isOpenUp {
          bottom: 56px;
        }
        &.isOpenDown {
          top: 55px;
        }
      }
    }
  }
`;

export default React.memo(ProjectSyncPartner);

interface NewSyncConfirmModalProps {
  partnerName: string;
  newPartnerName: string;
}
function NewSyncConfirmModal({ partnerName, newPartnerName }: NewSyncConfirmModalProps) {
  return (
    <StyledNewSyncConfirmModal data-component-name="NewSyncConfirmModal">
      <div className="newSyncConfirmModal__description">
        <p className="newSyncConfirmModal__description_record">
          <Trans
            defaults="NEW_SYNC_PARTNER_ALERT"
            // defaults={`<0>파트너 ${partnerName}에게 Sync된 기록이 있습니다.\n새로운 파트너 </0><1>${newPartnerName}</1><0>를 추가 하시겠습니까?</0>`}
            components={[
              <CustomSpan fontColor={'#444'}>{{ partnerName }}</CustomSpan>,
              <CustomSpan fontColor={'#00A6E2'}>{{ newPartnerName }}</CustomSpan>,
            ]}
          />
        </p>
        <p className="newSyncConfirmModal__description_noti">
          <img src={icon_circle_warning} />
          <CustomSpan>
            <T>NEW_SYNC_PARTNER_RESYNC</T>
          </CustomSpan>
        </p>
      </div>
    </StyledNewSyncConfirmModal>
  );
}

const StyledNewSyncConfirmModal = styled.div`
  .newSyncConfirmModal__description {
    white-space: break-spaces;
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    padding-bottom: 20px;
    .newSyncConfirmModal__description_record {
      line-height: 24px;
    }
    .newSyncConfirmModal__description_noti {
      display: flex;
      column-gap: 5px;
      align-items: center;
    }
  }
`;
